import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Award, Plus, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

// Discipline options shown in the type dropdown — alphabetised-ish but with
// the structured categories first so the most-common selections sit at the
// top. `other` lives at the very bottom and triggers a write-in input.
const TITLE_CATEGORY_OPTIONS = [
  { key: "puppy_class",  label: "Puppy class" },
  { key: "conformation", label: "Conformation" },
  { key: "obedience",    label: "Obedience" },
  { key: "rally",        label: "Rally" },
  { key: "agility",      label: "Agility" },
  { key: "dock_diving",  label: "Dock Diving" },
  { key: "farm_dog",     label: "Farm Dog" },
  { key: "cgc",          label: "CGC" },
  { key: "coursing",     label: "Coursing" },
  { key: "fastcat",      label: "FastCat" },
  { key: "scentwork",    label: "Scentwork" },
  { key: "barn_hunt",    label: "Barn Hunt" },
  { key: "gun_dog",      label: "Gun Dog / Retrieving" },
  { key: "other",        label: "Other" },
];

const CONFORMATION_ORGS = ["AKC", "UKC", "CKC", "IABCA"];

const categoryLabel = (key) =>
  (TITLE_CATEGORY_OPTIONS.find((o) => o.key === key) || {}).label || key;

function blankTitle(dogId) {
  return {
    dog_id: dogId,
    title_name: "",
    category: "",
    conformation_org: "",
    category_other: "",
    earned_date: new Date().toISOString().slice(0, 10),
    notes: "",
  };
}

export default function TitlesSection({ dog }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankTitle(dog.id));
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/titles", { params: { dog_id: dog.id } });
      setItems(data.items || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug("titles load failed", err);
    }
  };

  useEffect(() => { load(); setForm(blankTitle(dog.id)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dog.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title_name.trim() || !form.category || !form.earned_date) {
      toast.error("Title name, category and date are required");
      return;
    }
    if (form.category === "conformation" && !form.conformation_org) {
      toast.error("Pick the registry for the conformation title");
      return;
    }
    if (form.category === "other" && !form.category_other.trim()) {
      toast.error("Add a write-in for 'Other'");
      return;
    }
    setSaving(true);
    try {
      await api.post("/titles", form);
      toast.success("Title added");
      setForm(blankTitle(dog.id));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save title");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this title?")) return;
    try {
      await api.delete(`/titles/${id}`);
      load();
    } catch (err) { toast.error("Could not delete"); }
  };

  const isConformation = form.category === "conformation";
  const isOther = form.category === "other";

  return (
    <div data-testid="titles-section">
      <div className="flex items-center gap-2 mb-4 mt-2">
        <Award size={18} className="text-primary" />
        <h3 className="font-display text-xl font-bold tracking-tight">Titles</h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border shadow-none rounded-lg" data-testid="titles-list-card">
          <CardContent className="p-0">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground p-10 text-center" data-testid="titles-empty">
                No titles logged yet — add your first one →
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="titles-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-6 py-3">Earned</th>
                    <th className="px-6 py-3">Title</th>
                    <th className="px-6 py-3">Discipline</th>
                    <th className="px-6 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => {
                    let disciplineLabel = categoryLabel(t.category);
                    if (t.category === "conformation" && t.conformation_org) disciplineLabel = `Conformation · ${t.conformation_org}`;
                    if (t.category === "other" && t.category_other) disciplineLabel = `Other · ${t.category_other}`;
                    return (
                      <tr key={t.id} className="border-t border-border" data-testid={`title-row-${t.id}`}>
                        <td className="px-6 py-3 whitespace-nowrap">{format(parseISO(t.earned_date), "MMM d, yyyy")}</td>
                        <td className="px-6 py-3">
                          <p className="font-medium" data-testid={`title-name-${t.id}`}>{t.title_name}</p>
                          {t.notes && <p className="text-xs text-foreground/80 mt-1 whitespace-pre-wrap">{t.notes}</p>}
                        </td>
                        <td className="px-6 py-3">
                          <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary border border-primary/30 rounded-full px-2.5 py-0.5">
                            <Award size={10} /> {disciplineLabel}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`title-delete-${t.id}`} aria-label="Delete title">
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none rounded-lg" data-testid="add-title-card">
          <CardContent className="p-6">
            <p className="font-medium mb-4">Add title</p>
            <form onSubmit={submit} className="space-y-3" data-testid="add-title-form">
              <div>
                <Label htmlFor="title-name">Title name</Label>
                <Input
                  id="title-name"
                  value={form.title_name}
                  onChange={(e) => setForm({ ...form, title_name: e.target.value })}
                  placeholder="e.g. CGC, CD, RN, BCAT"
                  required
                  className="mt-1.5"
                  data-testid="title-name-input"
                />
              </div>
              <div>
                <Label htmlFor="title-date">Earned date</Label>
                <Input
                  id="title-date"
                  type="date"
                  value={form.earned_date}
                  onChange={(e) => setForm({ ...form, earned_date: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="title-date-input"
                />
              </div>
              <div>
                <Label htmlFor="title-category">Type</Label>
                <select
                  id="title-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value, conformation_org: "", category_other: "" })}
                  required
                  className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10"
                  data-testid="title-category-select"
                >
                  <option value="">— Choose —</option>
                  {TITLE_CATEGORY_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>
              {isConformation && (
                <div data-testid="title-conformation-sub">
                  <Label htmlFor="title-org">Registry</Label>
                  <select
                    id="title-org"
                    value={form.conformation_org}
                    onChange={(e) => setForm({ ...form, conformation_org: e.target.value })}
                    required
                    className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10"
                    data-testid="title-conformation-org"
                  >
                    <option value="">— Pick one —</option>
                    {CONFORMATION_ORGS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              )}
              {isOther && (
                <div data-testid="title-other-sub">
                  <Label htmlFor="title-other">Specify discipline</Label>
                  <Input
                    id="title-other"
                    value={form.category_other}
                    onChange={(e) => setForm({ ...form, category_other: e.target.value })}
                    placeholder="Discipline name"
                    required
                    className="mt-1.5"
                    data-testid="title-other-input"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="title-notes">Notes</Label>
                <Textarea
                  id="title-notes"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Judge, club, milestones..."
                  className="mt-1.5"
                  data-testid="title-notes-input"
                />
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-title-button"
              >
                <Plus size={14} className="mr-1.5" /> {saving ? "Saving…" : "Add title"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
